import type { UserMessage } from '@bt-common/schemas/bt/user-message';
import { WA_TOOL_COMMANDS } from '@bt-common/interfaces/whatsapp-tools-commands.interface';
import type { WaToolResponse } from '@bt-common/interfaces/whatsapp-tools-commands.interface';
import type { WaToolArgs } from '@bt-common/interfaces/whatsapp-tool-contracts.interface';
import { safeDispatch, getStateDirectly } from '@common/state/state-provider';
import { DBUserMessageThunks, userMessagesSelectOneObjectByQuery } from '@common-reducers/DBServiceThunks';
import { processWhatsappInterfaceCommand } from '@common/tools/whatsapp/wa-tools-notifier-handlers';
import { processFcmWake, type FcmWakeData, type FcmWakeOutcome } from './fcm-wake-message-resolver';

const isUserMessage = (m: unknown): m is UserMessage => typeof m === 'object' && m !== null && '_id' in m;

/**
 * FCM wake (parallel to MQTT): resolve the pending message, then feed it into the SAME wire handler
 * MQTT uses (the reply is not needed over FCM). Kept out of contentMessageService so the wiring to
 * `insertOnlyIfMissing` — the line that was once silently broken — is unit-testable.
 */
export function handleFcmInsertOnlyIfMissingWake(
  data: FcmWakeData | undefined,
): Promise<FcmWakeOutcome<WaToolResponse<unknown>>> {
  return processFcmWake<UserMessage, WaToolResponse<unknown>>(
    data,
    {
      getFromRedux: (id: string): UserMessage | undefined =>
        userMessagesSelectOneObjectByQuery(getStateDirectly().DBUserMessageReducer, { _id: id }),
      fetchById: async (id: string): Promise<UserMessage | undefined> => {
        const res: { payload?: unknown } = await safeDispatch(DBUserMessageThunks.find({ _id: id }));
        const list: unknown = res.payload;
        return Array.isArray(list) ? list.filter(isUserMessage).find((m) => m._id === id) : undefined;
      },
    },
    (messageData) => {
      console.log('[FCM] [context:content] 📨 Processing insertOnlyIfMissing via FCM (parallel to MQTT):', messageData._id);
      const args: WaToolArgs<typeof WA_TOOL_COMMANDS.insertOnlyIfMissing> = { message: messageData, checkStore: true };
      return processWhatsappInterfaceCommand(WA_TOOL_COMMANDS.insertOnlyIfMissing, args);
    },
  );
}
